import { useApolloClient } from '@apollo/client';
import * as _ from 'lodash-es';
import React from 'react';
import isNotDetached from './isNotDetached';
import SidebarExternalPostsSection from './SidebarExternalPostsSection';
import SidebarItems, { POST_FRAGMENT } from './SidebarItems';
import SidebarRow from './SidebarRow';
import SidebarSection, { CollapseBehavior } from './SidebarSection';
import SidebarSectionIcon from './SidebarSectionIcon';
import SidebarSectionOverflowMenuItems from './SidebarSectionOverflowMenuItems';
import * as styles from './SidebarSections.module.scss';
import {
  getSidebarSectionNameWithFallback,
  PremadeSections,
  useSidebarSectionsContext,
  useUpdateSidebarSectionIds,
} from './SidebarSectionsProvider';
import useAppSelector, { useAppDispatch } from '../app/redux/useAppSelector';
import SVGIcon from '../components/SVGIcon';
import Tooltip from '../components/Tooltip';
import { UserType } from '../graphql/generated/graphql';
import addIcon from '../icons/non-prefilled/16x16/add.svg';
import editIcon from '../icons/non-prefilled/16x16/edit.svg';
import { useAllSlimPostMap } from '../post/useAllSlimPosts';
import useCurrentPostTopicIds from '../post/useCurrentPostTopicIds';
import NewTopicModal from '../topic/modal/NewTopicModal';
import type { AllTopicsTreeListTopic } from '../topic/useAllTopics';
import useAllTopics, { useAllTopicMap } from '../topic/useAllTopics';
import { UIActions } from '../ui/uiSlice';
import addTestId from '../utils/addTestId';
import { useIsSize } from '../utils/responsive/isSize';
import Sizes from '../utils/responsive/Sizes';
import useSlug from '../utils/router/useSlug';

const CreateTopicButton = () => {
  const currentUser = useAppSelector((state) => state.session.user);
  const isGuest = currentUser.type === UserType.Guest;
  const dispatch = useAppDispatch();

  if (isGuest) return null;

  return (
    <div
      className={styles.addWrapper}
      onClick={(e) => {
        e.stopPropagation();
        dispatch(UIActions.openModal(NewTopicModal));
      }}
    >
      <Tooltip content="Create a new topic">
        <div {...addTestId('addTopicButton')} className={styles.add}>
          <SVGIcon size={9}>{addIcon}</SVGIcon>
        </div>
      </Tooltip>
    </div>
  );
};

interface SidebarAllTopicsProps {
  allTopics: AllTopicsTreeListTopic[];
  activeTopics: Record<string, boolean>;
  expandedTopics?: Record<string, boolean>;
  onMoveSectionUp?: () => void;
  onMoveSectionDown?: () => void;
}

const SidebarAllTopics = ({
  allTopics,
  activeTopics,
  expandedTopics,
  onMoveSectionUp,
  onMoveSectionDown,
}: SidebarAllTopicsProps) => {
  const isMobile = useIsSize(Sizes.SM);

  return (
    <SidebarSection
      id="allTopics"
      title="All Topics"
      icon={<SidebarSectionIcon section={{ id: PremadeSections.ALL_TOPICS }} />}
      collapseBehavior={
        expandedTopics
          ? CollapseBehavior.InitiallyExpanded
          : CollapseBehavior.AlwaysExpanded
      }
      menuItems={
        isMobile ? undefined : (
          <SidebarSectionOverflowMenuItems
            selectedSectionId={PremadeSections.ALL_TOPICS}
            onMoveSectionUp={onMoveSectionUp}
            onMoveSectionDown={onMoveSectionDown}
          />
        )
      }
      customMenu={<CreateTopicButton />}
    >
      <SidebarItems
        topics={allTopics}
        activeTopics={activeTopics}
        expandedTopics={expandedTopics}
        sectionId={PremadeSections.ALL_TOPICS}
      />
    </SidebarSection>
  );
};

interface SidebarMyTopicsProps {
  title: string;
  myTopics: AllTopicsTreeListTopic[];
  activeTopics: Record<string, boolean>;
  expandedTopics?: Record<string, boolean>;
  onMoveSectionUp?: () => void;
  onMoveSectionDown?: () => void;
}

const SidebarMyTopics = ({
  title,
  myTopics,
  activeTopics,
  expandedTopics,
  onMoveSectionUp,
  onMoveSectionDown,
}: SidebarMyTopicsProps) => {
  const isMobile = useIsSize(Sizes.SM);

  return (
    <SidebarSection
      id="myTopics"
      icon={<SidebarSectionIcon section={{ id: PremadeSections.MY_TOPICS }} />}
      title={title}
      collapseBehavior={
        expandedTopics
          ? CollapseBehavior.InitiallyExpanded
          : CollapseBehavior.AlwaysExpanded
      }
      menuItems={
        isMobile ? undefined : (
          <SidebarSectionOverflowMenuItems
            selectedSectionId={PremadeSections.MY_TOPICS}
            onMoveSectionUp={onMoveSectionUp}
            onMoveSectionDown={onMoveSectionDown}
          />
        )
      }
      customMenu={<CreateTopicButton />}
    >
      <SidebarItems
        topics={myTopics}
        orderIds={myTopics.map((topic) => topic.id)}
        activeTopics={activeTopics}
        expandedTopics={expandedTopics}
        sectionId={PremadeSections.MY_TOPICS}
      />
    </SidebarSection>
  );
};

type TopicStates = {
  active?: Record<string, boolean>;
  expanded?: Record<string, boolean>;
};

const getTopicStates = (
  activeId: string,
  topics: AllTopicsTreeListTopic[],
): TopicStates | null =>
  topics.reduce((result: TopicStates | null, { id, childTopics }) => {
    if (result) return result;

    if (activeId === id) {
      return { active: { [id]: true } };
    }

    const childResult = getTopicStates(activeId, childTopics);

    if (childResult) {
      return {
        active: childResult.active,
        expanded: {
          [id]: true,
          ...childResult.expanded,
        },
      };
    }

    return null;
  }, null);

const getAllTopicStates = (ids: string[], topics: AllTopicsTreeListTopic[]) => {
  const states = ids.map((id) => getTopicStates(id, topics));
  return _.merge({ expanded: {}, active: {} }, ...states);
};

const SidebarSections = () => {
  const [topicId] = useSlug('topicSlug');
  const { sidebarSections, myTopics, allTopics, permissions } =
    useSidebarSectionsContext();
  const postTopicIds = useCurrentPostTopicIds();

  const treeTopics = useAllTopics();

  const activeTopicIds = topicId ? [topicId] : postTopicIds;
  const { expanded, active: activeTopics } = getAllTopicStates(
    activeTopicIds,
    treeTopics,
  );

  const expandedTopics = permissions.canHideSections ? expanded : undefined;

  const dispatch = useAppDispatch();
  const allTopicsMap = useAllTopicMap();
  const allSlimPostsMap = useAllSlimPostMap();
  const isMobile = useIsSize(Sizes.SM);

  const [updateSidebarSectionIds] = useUpdateSidebarSectionIds();

  async function moveSection(
    section: (typeof sidebarSections)[number],
    direction: 'down' | 'up',
  ) {
    try {
      const index = sidebarSections.findIndex((item) => item.id === section.id);
      const newIndex = index + (direction === 'up' ? -1 : 1);
      if (index < 0 || newIndex < 0 || newIndex >= sidebarSections.length) {
        return;
      }

      const sectionIds = sidebarSections
        .toSpliced(index, 1)
        .toSpliced(newIndex, 0, section)
        .map((item) => item.id);
      await updateSidebarSectionIds({ variables: { sectionIds } });
    } catch (error) {
      dispatch(
        UIActions.pushToast({
          level: 'error',
          message: 'Could not save section order due to an error',
        }),
      );
    }
  }

  const getMoveSectionHandlers = (
    section: (typeof sidebarSections)[number],
  ) => ({
    onMoveSectionUp:
      sidebarSections.at(0)?.id === section.id
        ? undefined
        : () => moveSection(section, 'up'),
    onMoveSectionDown:
      sidebarSections.at(-1)?.id === section.id
        ? undefined
        : () => moveSection(section, 'down'),
  });

  const client = useApolloClient();

  return (
    <>
      {sidebarSections.length === 0 && (
        <SidebarRow
          icon={editIcon}
          onClick={() =>
            dispatch(UIActions.setSelectedSectionId(PremadeSections.MY_TOPICS))
          }
        >
          Manage sections
        </SidebarRow>
      )}
      {sidebarSections.map((section) => {
        if (section.id === PremadeSections.ALL_TOPICS) {
          return (
            <SidebarAllTopics
              key={PremadeSections.ALL_TOPICS}
              allTopics={allTopics}
              expandedTopics={expandedTopics}
              activeTopics={activeTopics}
              {...getMoveSectionHandlers(section)}
            />
          );
        }

        if (section.id === PremadeSections.MY_TOPICS) {
          return (
            <SidebarMyTopics
              key={PremadeSections.MY_TOPICS}
              title={section.name}
              myTopics={myTopics}
              expandedTopics={expandedTopics}
              activeTopics={activeTopics}
              {...getMoveSectionHandlers(section)}
            />
          );
        }

        if (section.id === PremadeSections.INTEGRATIONS) {
          return (
            <SidebarExternalPostsSection
              key={PremadeSections.INTEGRATIONS}
              collapsible={!!expandedTopics}
              {...getMoveSectionHandlers(section)}
            />
          );
        }

        const posts = _.compact(
          section.items
            .filter((item) => item.__typename === 'Post')
            .map((item) => {
              const cachedPost =
                client.readFragment({
                  fragment: POST_FRAGMENT,
                  id: client.cache.identify({
                    __typename: 'Post',
                    id: item.id,
                  }),
                  fragmentName: 'SidebarItems_post',
                }) ?? allSlimPostsMap[item.id];

              if (!cachedPost) return null;
              return {
                ...cachedPost,
                title: cachedPost.title || 'Untitled',
              };
            }),
        );

        const topics = _.compact(
          section.items.map((item) =>
            item.__typename === 'Topic' ? allTopicsMap[item.id] : null,
          ),
        ).filter(isNotDetached);

        return (
          <SidebarSection
            key={section.id}
            id={section.id}
            title={getSidebarSectionNameWithFallback(section.name)}
            collapseBehavior={
              expandedTopics
                ? CollapseBehavior.InitiallyCollapsed
                : CollapseBehavior.AlwaysExpanded
            }
            icon={<SidebarSectionIcon section={section} />}
            menuItems={
              !isMobile && (
                <SidebarSectionOverflowMenuItems
                  selectedSectionId={section.id}
                  {...getMoveSectionHandlers(section)}
                />
              )
            }
          >
            <SidebarItems
              activeTopics={activeTopics}
              expandedTopics={expandedTopics}
              posts={posts}
              orderIds={section.items.map((item) => item.id)}
              topics={topics}
              sectionId={section.id}
            />
          </SidebarSection>
        );
      })}
    </>
  );
};

export default SidebarSections;
